import React, { useCallback } from "react"
import styled, { css } from "styled-components"
import Breakpoints from "../../components/shared/breakpoints"
import { Block, Spacer } from "../atoms/layout-primitives"
import TagSelect, { SelectCaret } from "../atoms/tag-select"
import VariantContainer from "../atoms/variant-container"
import { BT } from "../styles/blog-typography"
import { IContentfulTag } from "../types/blog.types"

const Title = styled.label`
  display: block;
  ${BT.sm.info`500`};
  font-size: 12px;
  opacity: 0.45;
  text-transform: uppercase;
  @media (min-width: ${Breakpoints.md}) {
    display: none;
  }
`

const Tag = styled.button<{ $active: boolean; $large: boolean }>`
  ${BT.sm.info`400`};
  font-size: ${(props) => (props.$large ? "16px" : "14px")};
  padding: ${(props) => (props.$large ? `8px 16px` : `4px 8px`)};
  background-color: ${(props) =>
    props.$active ? "var(--burnt-orange)" : "var(--white)"};
  color: ${(props) => (props.$active ? "var(--white)" : "var(--slate)")};
  border: 1px solid
    ${(props) => (props.$active ? "var(--burnt-orange)" : "#d9d9d9")};
  border-radius: 2px;
  cursor: pointer;
  :hover,
  :focus,
  :active {
    background-color: var(--burnt-orange);
    color: var(--white);
  }

  transition: all 0.2s;
`

interface ITagFilterProps {
  title?: String
  tags: IContentfulTag[]
  id?: string
  name?: string
  onClick: any
  large?: boolean
  active?: string
}

const AllTag = { name: "All", slug: "all" }

/**
 * Tag filtering buttons that condense to a dropdown on mobile.
 * @param title - the title above the select on mobile
 * @param tags - the tags to filter by
 * @param id - the id of the select
 * @param name - the name of the select and name of the tag filter
 * @param onClick - the onClick handler
 * @param large - increase the size of the tag buttons
 * @param active - the active tag
 * */
const TagFilter = ({
  title,
  tags,
  id = "tags",
  name = "Tags",
  onClick,
  large = false,
  active = AllTag.name,
}: ITagFilterProps) => {
  const [activeFilter, setActiveFilter] = React.useState(active)
  const handleFilterChanged = useCallback(
    (tagName: string) => {
      setActiveFilter(tagName)
      // run the passed onclick function
      onClick(tagName)
    },
    [setActiveFilter, onClick]
  )

  return (
    <>
      {title && (
        <Block display={{ md: "none" }}>
          <Title htmlFor={id}>{title}</Title>
          <Spacer height={{ sm: "12px" }} />
        </Block>
      )}
      {/* Mobile dropdown menu */}
      <Block
        display={{ md: "none" }}
        variants={[
          css`
            position: relative;
          `,
        ]}
      >
        <SelectCaret />
        <TagSelect
          id={id}
          name={name.toLowerCase()}
          value={activeFilter}
          onChange={(e) => handleFilterChanged(e.target.value)}
        >
          <option value={AllTag.name}>All {name}</option>
          {tags.map((tag) => (
            <option key={tag.slug} value={tag.name}>
              {tag.name}
            </option>
          ))}
        </TagSelect>
      </Block>
      {/* Tablet/desktop tag buttons */}
      <VariantContainer
        variants={[
          css`
            display: none;
          `,
          css`
            display: block;
            overflow-x: auto;
          `,
        ]}
      >
        <VariantContainer
          variants={[
            css`
              display: flex;
              gap: ${large ? `0` : `12px`};
              width: max-content;
            `,
            null,
            css`
              width: 100%;
              flex-wrap: wrap;
            `,
          ]}
        >
          <Tag
            $active={activeFilter === AllTag.name}
            $large={large}
            onClick={() => handleFilterChanged(AllTag.name)}
          >
            All {name}
          </Tag>
          {tags.map((tag) => (
            <Tag
              $active={activeFilter === tag.name}
              $large={large}
              key={tag.slug}
              onClick={() => handleFilterChanged(tag.name || AllTag.name)}
            >
              {tag.name}
            </Tag>
          ))}
        </VariantContainer>
      </VariantContainer>
    </>
  )
}

export default TagFilter
